import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {Row, Col, Button, Input, Select, Table, Spin, Checkbox, notification, Tooltip, Modal} from 'antd';
import {
  userList,
  changeAccessUserPart,
  changeUserStatus,
  changeUserState,
  changeUserPermissions,
  changeUserRole,
  deleteUser
} from '../../services/userApi';
import {getAllStates} from '../../services/adminApi';
import {EditOutlined, DeleteOutlined} from "@ant-design/icons";
import {PermissionsModal} from "../../components/PermissionsModal";
import { useAuth } from '../../hooks/auth';

import './Users.css';

interface TreeNode {
  label: string;
  value: number;
  disabled?: boolean;
  children?: TreeNode[];
}

interface State {
  id: number;
  parentId: number;
  name: string;
  type: string;
  updatedAt: string;
  createdAt: string;
}

const Users: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [roles, setRoles] = useState<any>({ 0: { id: 0, name: 'Пользователь' } });
  const [allRoles, setAllRoles] = useState<Array<{ id: number, name: string }>>([]);
  const [search, setSearch] = useState('');
  const [state, setState] = useState<number>(0);
  const [status, setStatus] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<string>('lp');
  const [editingUser, setEditingUser] = useState<any>(null);

  const { user } = useAuth();

  const hasEditUserStatusPermission = user.extraData?.permissions?.editUserStatus && user.type === 'registered';

  const openPermissionsModal = (user: any) => {
    setEditingUser(user);
  };

  const closeModal = () => {
    setEditingUser(null);
  };

  const savePermissions = async (userId: number, newPermissions: Record<string, boolean> | null) => {
    try {
      setIsLoading(true);

      await changeUserPermissions(userId, newPermissions);

      setUsers(prevUsers =>
          prevUsers.map(user => user.id === userId ? { ...user, permissions: newPermissions } : user)
      );

      notification.success({ message: 'Права успешно обновлены' });
    } catch (error) {
      notification.error({ message: 'Ошибка при изменении прав' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
    
      const searchQuery = searchParams.get('search');
      if (searchQuery) {
        setSearch(searchQuery.toLowerCase());
        setViewMode('registered');
        setSearchParams({});
      } else {
        setViewMode(hasEditUserStatusPermission ? 'registered' : 'lp');
      }
      
      try {
        const [userResult, statesResult] = await Promise.all([
          userList(),
          !hasEditUserStatusPermission ? getAllStates() : Promise.resolve([])
        ]);

        setUsers(userResult.users.map((x: any) => ({ ...x, key: x.id })));

        const allowedRoleIds = [4, 5, 6, 9, 10, 11, 12];
        const rolesWithSeller = { ...userResult.roles };

        if (!rolesWithSeller[0]) {
          rolesWithSeller[0] = { id: 0, name: 'Продавец' };
        }

        setRoles((prevRoles: any) => ({...prevRoles, ...rolesWithSeller}));

        const filteredRolesArray = (Object.values(rolesWithSeller) as Array<{ id: number; name: string }>).filter((role) => [...allowedRoleIds, 0].includes(role.id));
        setAllRoles(filteredRolesArray);

        if (!hasEditUserStatusPermission && statesResult) {
          setStates(statesResult.sort((a, b) => a.name.localeCompare(b.name)));
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const processTreeData = (tree: any[]): TreeNode[] => {
    return tree.map(node => ({
      ...node,
      disabled: node.type !== 'FS',
      children: node.children ? processTreeData(node.children) : [],
    }));
  };

  const filterUsers = (users: any) => {
    let result = users;
    if (search.length >= 3) result = result.filter((user: any) => user.name?.toLowerCase()?.indexOf(search) >= 0 || user.email?.toLowerCase()?.indexOf(search) >= 0 || user.fio?.toLowerCase()?.indexOf(search) >= 0 || user.phone?.toLowerCase()?.indexOf(search) >= 0);
    if (state) result = result.filter((user: any) => user.stateId === state);
    if (status && viewMode === 'registered') result = result.filter((user: any) => user.status === status);
    return hasEditUserStatusPermission ? result : result.filter((user: { type: string; }) => viewMode === 'registered' ? user.type === 'registered' : user.type !== 'registered');
  }

  const setAccessUserPart = async (userId: number, status: boolean) => {
    if(await changeAccessUserPart(userId, status)) {
      const newUsers = users.map((user:any)=>{
        if(user.id === userId) return {...user, accessUserPart: status};
        return user;
      })
      setUsers(newUsers);
    } else notification.error({ message: 'Произошла ошибка при сохранении' })
  }

  const handleStatusChange = async (userId: number, newStatus: string) => {
    try {
      setIsLoading(true);

      await changeUserStatus(userId, newStatus);

      setUsers(prevUsers =>
          prevUsers.map(user =>
              user.id === userId ? { ...user, status: newStatus } : user
          )
      );

      notification.success({ message: 'Статус успешно обновлён' });
    } catch (error) {
      notification.error({ message: 'Ошибка при изменении статуса' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: number) => {
    try {
      setIsLoading(true);

      await changeUserRole(userId, newRole);

      setUsers(prevUsers =>
          prevUsers.map(user =>
              user.id === userId ? { ...user, role: newRole } : user
          )
      );

      notification.success({ message: 'Роль успешно обновлена' });
    } catch (error) {
      notification.error({ message: 'Ошибка при изменении роли' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStateChange = async (userId: number, stateId: number) => {
    try {
      setIsLoading(true);
      const success = await changeUserState(userId, stateId);
      if (success) {
        setUsers(prevUsers =>
            prevUsers.map(user =>
                user.id === userId ? { ...user, stateId: stateId } : user
            )
        );
        notification.success({ message: 'Филиал успешно изменен' });
      } else {
        notification.error({ message: 'Ошибка при изменении филиала' });
      }
    } catch (error) {
      notification.error({ message: 'Ошибка при изменении филиала' });
    } finally {
      setIsLoading(false);
    }
  };

  const lpUsersColumns = [
    {
      title: 'Имя пользователя',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Баланс',
      dataIndex: 'balance',
      key: 'balance',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Филиал',
      dataIndex: 'stateName',
      key: 'stateName',
      sorter: (a: any, b: any) => (a.stateName ?? '').localeCompare(b.stateName ?? ''),
      render: (stateName: string, row: any) => {
        if (row.type === 'registered') {
          return (
              <Select
                  defaultValue={row.stateId}
                  style={{ width: '100%' }}
                  onChange={(value) => handleStateChange(row.id, value)}
                  options={[
                    { label: 'Все филиалы', value: 0 },
                    ...states.map((state: any) => ({ label: state.name, value: state.id })),
                  ]}
              />
          );
        }
        return stateName;
      },
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Дата регистрации',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Пользовательский доступ',
      dataIndex: 'accessUserPart',
      key: 'accessUserPart',
      align: 'center' as const,
      render: (val: boolean, row: any) =>  [2,3,4,5,6].includes(row.role) ? <Checkbox checked={!row.role || val} onChange={(e)=>setAccessUserPart(row.id, e.target.checked)}/> : row.access===0?'Заблокирован':'Активен'
    },
  ];

  const registeredUsersColumns = [
    {
      title: 'Баланс',
      dataIndex: 'balance',
      key: 'balance',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Филиал',
      dataIndex: 'stateName',
      key: 'stateName',
      sorter: (a: any, b: any) => (a.stateName ?? '').localeCompare(b.stateName ?? ''),
      render: (stateName: string, row: any) => {
        if (row.type === 'registered' && !hasEditUserStatusPermission) {
          return (
              <Select
                  defaultValue={row.stateId}
                  style={{ width: '100%' }}
                  onChange={(value) => handleStateChange(row.id, value)}
                  options={[
                    { label: 'Все филиалы', value: 0 },
                    ...states.map((state: any) => ({ label: state.name, value: state.id })),
                  ]}
              />
          );
        }
        return stateName;
      },
    },
    {
      title: 'Город',
      dataIndex: 'city',
      key: 'city',
      render: (city: string) => city || 'Отсутствует',
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Права доступа',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (_val: any, row: any) => {
        const hasPermissions = row.permissions ? Object.keys(row.permissions).length > 0 : false;

        return (
          <div className="users-role-column">
            <span>{hasPermissions ? 'Админ' : 'Отсутствует'}</span>
            {!hasEditUserStatusPermission && (
              <Tooltip title="Редактировать права">
                <EditOutlined
                  className="users-action-button"
                  onClick={() => openPermissionsModal(row)}
                />
              </Tooltip>
            )}
          </div>
        );
      }
    },

    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      render: (roleValue: number, row: any) => (
        <Select
          defaultValue={roleValue}
          style={{ minWidth: 220 }}
          onChange={(value) => handleRoleChange(row.id, value)}
          options={allRoles.map((role) => ({
            value: role.id,
            label: role.id === 0 ? 'Продавец' : role.name
          }))}
        />
      )
    },

    {
      title: 'Дата регистрации',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Пользовательский доступ',
      dataIndex: 'accessUserPart',
      key: 'accessUserPart',
      align: 'center' as const,
      render: (val: boolean, row: any) => {
        return (
          <div className="users-access-column">
            {[2,3,4,5,6].includes(row.role) || (row.type === 'registered' && !hasEditUserStatusPermission)
              ? <Checkbox
                  checked={(!row.role && row.type !== 'registered') || val}
                  onChange={(e)=>setAccessUserPart(row.id, e.target.checked)}
                />
              : row.access === 0 ? 'Заблокирован' : 'Активен'}
          </div>
        );
      }
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, row: any) => {
        const statusLabels: { [key: string]: string } = {
          'pending': 'Ожидает подтверждения',
          'active': 'Активен',
          'blocked': 'Заблокирован',
        };

        if (row.type === 'registered') {
          return (
              <Select
                  defaultValue={status}
                  style={{ width: '100%' }}
                  onChange={(value) => handleStatusChange(row.id, value)}
                  options={[
                    { label: 'Ожидает подтверждения', value: 'pending' },
                    { label: 'Активен', value: 'active' },
                    { label: 'Заблокирован', value: 'blocked' },
                  ]}
              />
          );
        }

        return statusLabels[status] || 'Неизвестно';
      },
    },
  ];

  if (user.type === 'admin') {
    registeredUsersColumns.push(
      {
        title: 'Действия',
        dataIndex: 'actions',
        key: 'actions',
        align: 'center' as const,
        render: (_: any, row: any) => (
          <div className="users-action-buttons">
            <Tooltip title="Удаление пользователя">
              <DeleteOutlined
                className="users-action-button"
                onClick={() => {
                  Modal.confirm({
                    title: 'Удаление пользователя',
                    content: `Вы уверены, что хотите удалить пользователя "${row.fio || row.name || ''}"?`,
                    onOk: () => handleDeleteUser(row),
                    okText: 'Да',
                    cancelText: 'Отмена',
                    maskClosable: true,
                  });
                }}
              />
            </Tooltip>
          </div>
        ),
      }
    );
  };

  const handleDeleteUser = async (userToDelete: any) => {
    try {
      await deleteUser(userToDelete.id);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      notification.success({ message: 'Пользователь успешно удалён' });
    } catch (error) {
      notification.error({ message: 'Ошибка при удалении пользователя' });
    }
  };

  const showingUsers = filterUsers(users);

  return (
    <Spin spinning={isLoading}>
      <Row justify="space-between" align="middle" style={{ marginTop: 40 }}>
        <Col>
        {hasEditUserStatusPermission ? (
        <Row>
          <Col>
            <div className="users-page-title">Свободная регистрация</div>
          </Col>
        </Row>
      ) : (
        <div className="users-tab-navigation">
        <span
            className={`users-tab-item ${viewMode === 'lp' ? 'users-tab-item-active' : ''}`}
            onClick={() => setViewMode('lp')}
        >
          Пользователи ЛП
        </span>
        <span
            className="users-tab-separator"
        >
          /
        </span>
        <span
            className={`users-tab-item ${viewMode === 'registered' ? 'users-tab-item-active' : ''}`}
            onClick={() => setViewMode('registered')}
        >
          Свободная регистрация
        </span>
      </div>
      )}
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 20 }} >
        <Col sm={hasEditUserStatusPermission ? 12 : 8} xs={24}>Поиск:<Input value={search} style={{ marginTop: 7 }} placeholder='Имя пользователя / E-mail / ФИО / Телефон' onChange={(e) => { setSearch(e.target.value.toLowerCase()) }} /></Col>
        {!hasEditUserStatusPermission &&
          <Col sm={8} xs={24}>Филиал:
            <Select
              style={{ marginTop: 7, width: '100%' }}
              placeholder="Все филиалы"
              options={[{ label: 'Все филиалы', value: 0 }, ...states.map(x => ({ value: x.id, label: x.name }))]}
              onChange={setState}
              />
          </Col>
        }
        {viewMode === 'registered' && (
          <Col sm={hasEditUserStatusPermission ? 12 : 8} xs={24}>Статус:
            <Select
                style={{ marginTop: 7, width: '100%' }}
                defaultValue=""
                options={[
                  { label: 'Все статусы', value: '' },
                  { label: 'Ожидает подтверждения', value: 'pending' },
                  { label: 'Активен', value: 'active' },
                  { label: 'Заблокирован', value: 'blocked' },
                ]}
                onChange={setStatus}
            />
          </Col>
        )}
        <Col span={24}>
          <div className="users-count">Всего пользователей: {showingUsers.length}</div>
          <Table
            scroll={{ x: 1200 }}
            columns={viewMode === 'registered' ? registeredUsersColumns : lpUsersColumns}
            dataSource={showingUsers}
            pagination={{
              showSizeChanger: true,
              locale: {
                items_per_page: 'стр',
                jump_to: 'Перейти',
                jump_to_confirm: 'OK',
                page: 'стр',
              }
            }}
          />
        </Col>
      </Row>
      {!hasEditUserStatusPermission && (
        <>
          <PermissionsModal
            user={editingUser}
            open={!!editingUser}
            onClose={closeModal}
            onSave={savePermissions}
          />
        </>
      )}
    </Spin>
  );
};

export default Users;
